// 6-step pairing wizard for a new kimi-worker device.

export interface WorkerPairingWizardProps {
  step: 1 | 2 | 3 | 4 | 5 | 6;
  token: string | null;
  projectorUrl: string;
  onGenerateToken: () => void;
  onNextStep: () => void;
}

export function WorkerPairingWizard({
  step,
  token,
  projectorUrl,
  onGenerateToken,
  onNextStep,
}: WorkerPairingWizardProps) {
  return (
    <div className="bg-card border border-border rounded-md p-6 max-w-2xl">
      <div className="text-foreground-mute text-sm mb-2">Step {step} of 6</div>

      {step === 1 && (
        <>
          <h2 className="text-foreground font-semibold mb-3">Install Kimi Desktop App</h2>
          <p className="text-foreground-mute text-sm mb-4">
            Download the Kimi Desktop App from <span className="font-mono">kimi.com/features/webbridge</span>.
            macOS and Windows are supported.
          </p>
          <button onClick={onNextStep} className="bg-primary text-primary-foreground px-4 py-2 rounded">
            Done — next
          </button>
        </>
      )}

      {step === 2 && (
        <>
          <h2 className="text-foreground font-semibold mb-3">Install Chrome extension</h2>
          <p className="text-foreground-mute text-sm mb-4">
            Add the Kimi WebBridge extension to your Chrome from the Chrome Web Store.
          </p>
          <button onClick={onNextStep} className="bg-primary text-primary-foreground px-4 py-2 rounded">
            Done — next
          </button>
        </>
      )}

      {step === 3 && (
        <>
          <h2 className="text-foreground font-semibold mb-3">Log into X.com</h2>
          <p className="text-foreground-mute text-sm mb-4">
            Open x.com in that Chrome browser and sign in. Your session stays local — Kimi never transmits cookies off-device.
          </p>
          <button onClick={onNextStep} className="bg-primary text-primary-foreground px-4 py-2 rounded">
            Done — next
          </button>
        </>
      )}

      {step === 4 && (
        <>
          <h2 className="text-foreground font-semibold mb-3">Install Bun + fetch the worker</h2>
          <p className="text-foreground-mute text-sm mb-2">Install Bun:</p>
          <pre className="bg-muted text-foreground text-xs p-3 rounded mb-3 font-mono">curl -fsSL https://bun.sh/install | bash</pre>
          <p className="text-foreground-mute text-sm mb-2">Clone the repo (or fetch the kimi-worker bundle):</p>
          <pre className="bg-muted text-foreground text-xs p-3 rounded mb-3 font-mono">git clone &lt;repo-url&gt;</pre>
          <button onClick={onNextStep} className="bg-primary text-primary-foreground px-4 py-2 rounded">
            Done — next
          </button>
        </>
      )}

      {step === 5 && !token && (
        <>
          <h2 className="text-foreground font-semibold mb-3">Pair this device</h2>
          <p className="text-foreground-mute text-sm mb-4">
            Click below to generate a one-time pairing token. The token will be shown ONCE — copy it now.
          </p>
          <button onClick={onGenerateToken} className="bg-primary text-primary-foreground px-4 py-2 rounded">
            Generate pairing token
          </button>
        </>
      )}

      {step === 5 && token && (
        <>
          <h2 className="text-foreground font-semibold mb-3">Token generated — copy now</h2>
          <p className="text-foreground-mute text-sm mb-2">Paste this command into the terminal where you cloned the repo:</p>
          <pre className="bg-muted text-foreground text-xs p-3 rounded mb-4 font-mono whitespace-pre-wrap">
{`KIMI_WORKER_ID=$(hostname) \\
KIMI_WORKER_TOKEN=${token} \\
PROJECTOR_URL=${projectorUrl} \\
bun run --cwd apps/kimi-worker start`}
          </pre>
          <p className="text-danger text-xs mb-3">
            ⚠ This token will not be shown again. Store it now or rotate via "Pair new device" if lost.
          </p>
          <button onClick={onNextStep} className="bg-primary text-primary-foreground px-4 py-2 rounded">
            I have started the worker — verify
          </button>
        </>
      )}

      {step === 6 && (
        <>
          <h2 className="text-foreground font-semibold mb-3">Verify connection</h2>
          <p className="text-foreground-mute text-sm">
            Refresh the workers page to see your device listed. Heartbeats arrive every 30 seconds.
          </p>
        </>
      )}
    </div>
  );
}
